import type { DailyScore, GameManifest } from "./manifest.js";
import type { MatchOutcome } from "./rules.js";

/**
 * The daily (P4-TANK-03, founder 2026-10-22): one mission and one seed per game per UTC day, the
 * same for everyone, so friends can compare. The rules are already seeded, so the same seed and
 * mission give everyone the same board, words or waves. A game may list which missions can be the
 * daily (`manifest.daily`); otherwise every free mission that isn't a tutorial, a battle or an
 * extra opened after another (a run) can be.
 */

/** FNV-1a, 32 bits: a stable number from text. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** The first daily (P4-TANK-03 shipped 2026-10-23): daily #1. */
export const FIRST_DAILY = "2026-10-23";

/**
 * A day's daily number, the same in every game (ADR 0041): "Beanspot daily #7". Counted in UTC days
 * from the first daily; 0 before it.
 */
export function dailyNumber(day: string): number {
  const days = Math.round(
    (Date.parse(`${day}T00:00:00Z`) - Date.parse(`${FIRST_DAILY}T00:00:00Z`)) / 86_400_000,
  );
  return Number.isFinite(days) && days >= 0 ? days + 1 : 0;
}

/** A UTC day as YYYY-MM-DD. */
export const dayOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** What the daily reads of a game: its id, its missions, and its own list if any. */
export type DailySource = Pick<GameManifest, "id"> & {
  daily?: readonly string[] | undefined;
  missions: readonly {
    id: string;
    pack?: string | undefined;
    tutorial?: boolean | undefined;
    battle?: boolean | undefined;
    opensAfter?: string | undefined;
  }[];
};

/** The missions that can be a game's daily. */
export function dailyMissions(manifest: DailySource): readonly string[] {
  if (manifest.daily?.length) return manifest.daily;
  return manifest.missions
    .filter((m) => !m.pack && !m.tutorial && !m.battle && !m.opensAfter)
    .map((m) => m.id);
}

/** The game's daily for a UTC day: its mission and seed. Null if the game has no eligible mission. */
export function dailyOf(
  manifest: DailySource,
  day: string,
): { day: string; missionId: string; seed: number } | null {
  const missions = dailyMissions(manifest);
  if (missions.length === 0) return null;
  const missionId = missions[hash(`${manifest.id}:${day}:mission`) % missions.length] ?? "";
  return { day, missionId, seed: hash(`${manifest.id}:${day}:seed`) % 2 ** 31 };
}

/** A tier's lowest share of `of`, from the second tier up (ADR 0041): 40%, 60%, 80%, all. */
const SHARE_AT = [0.4, 0.6, 0.8, 1] as const;

/**
 * Where a daily's outcome falls among its game's five tiers (ADR 0041), 0 the lowest. With `of`,
 * the share of it reached; without, the result and stars: a win with one, two or three stars is
 * the third, fourth or fifth, a loss that got going the second, nothing at all the first. Either
 * way it's fair across missions with different goals, so any mission can be the daily.
 */
export function dailyTier(
  score: Pick<DailyScore, "stat" | "of">,
  outcome: Pick<MatchOutcome, "result" | "stats">,
): number {
  const value = Math.max(0, outcome.stats[score.stat] ?? 0);
  if (score.of !== undefined) {
    const of = outcome.stats[score.of] ?? 0;
    const share = of > 0 ? value / of : 0;
    return SHARE_AT.filter((at) => share >= at).length;
  }
  if (outcome.result === "win")
    return 1 + Math.min(3, Math.max(1, Math.round(outcome.stats["stars"] ?? 1)));
  return value > 0 ? 1 : 0;
}

/** The daily's score in words (ADR 0041): "14 rows", or "9 of 10 differences". */
export function dailyScoreText(
  score: Pick<DailyScore, "stat" | "of" | "unit">,
  stats: Readonly<Record<string, number>>,
): string {
  const value = Math.max(0, Math.round(stats[score.stat] ?? 0));
  const of = score.of === undefined ? undefined : Math.round(stats[score.of] ?? 0);
  const [one, many] = score.unit;
  return of === undefined
    ? `${String(value)} ${value === 1 ? one : many}`
    : `${String(value)} of ${String(of)} ${of === 1 ? one : many}`;
}
